import { useModalMeta } from "@/shared/atoms/useModal";
import useScheduleDepositMutation from "@/shared/mutations/schedules/useScheduleDepositMutation";
import {
  depositsToRows,
  formatDate,
  formatPrice,
} from "@/shared/utils/helpers";
import { DepositRow, Schedule } from "@/shared/utils/interfaces";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Button from "../Button/Button";
import Textfield from "../Input/Textfield";
import Modal, { ModalProps } from "../Modal/Modal";
import Table from "../Table/Table";
import { TableColumn } from "../Table/types";

const ScheduleDepositModal = ({ onExit, ...props }: ModalProps) => {
  const deposit = useScheduleDepositMutation();
  const [meta] = useModalMeta();
  const schedule: Schedule = meta.schedule;
  const { t } = useTranslation("");
  const { register, errors, handleSubmit } = useForm();
  const onSubmit = ({ partial_deposit }) => {
    deposit
      .mutateAsync({
        item_id: schedule.item_id,
        account_id: schedule.account_id,
        partial_deposit,
      })
      .then(() => {
        meta?.refetch();
        onExit();
      });
  };

  const columns = useMemo<TableColumn<DepositRow>[]>(
    () => [
      {
        Header: t("newsletter.date"),
        accessor: "date",
        id: "date",
        Cell: (row) => formatDate(row.date, "dateTime"),
      },
      {
        Header: t("depositAmount"),
        accessor: "value",
        id: "value",
        Cell: (row) => formatPrice(row.value),
      },
    ],
    [t]
  );

  return (
    <Modal width={{ _: "95%", sm: "50rem" }} onExit={onExit} {...props}>
      <form onSubmit={handleSubmit(onSubmit)} className="my-[3rem]">
        <h2>{t("depositMoneyFor", { name: schedule.name })}</h2>
        <div className="mb-[3rem]">
          <Table
            id="item_id"
            tableName="deposits"
            data={depositsToRows(schedule.partial_deposit)}
            columns={columns}
            disableSort
            disablePagination
            disableFilter
            disableAction
          />
        </div>
        <Textfield
          ref={register({
            required: {
              value: true,
              message: t("fieldRequired"),
            },
          })}
          name="partial_deposit"
          error={errors?.partial_deposit?.message}
          label={t("depositAmount")}
          placeholder={t("depositAmountPlaceholder", { price: formatPrice(5) })}
        />
        <div className="mt-8 flex gap-12">
          <div>
            <Button isPending={deposit.isPending}>{t("deposit")}</Button>
          </div>

          <Button onClick={onExit}>{t("cancel")}</Button>
        </div>
      </form>
    </Modal>
  );
};

export default ScheduleDepositModal;
